import React, { useContext, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import Modal, { Styles } from "react-modal";
import * as _ from "lodash-es";
import { Socket } from "socket.io-client";
import SupplierContext from "../../../../contexts/SupplierContext";
import UserContext from "../../../../contexts/UserContext";
import IErrorDTO from "../../../../models/DTO/IErrorDTO";
import IGetWorkflowsDTO from "../../../../models/DTO/IGetWorkflowsDTO";
import ILoadUserDTO from "../../../../models/DTO/ILoadUserDTO";
import IOnGetWorkflowsDTO from "../../../../models/DTO/IOnGetWorkflowsDTO";
import IProcessWorkflowDTO from "../../../../models/DTO/IProcessWorkflowDTO";
import EndpointsEnum from "../../../../models/EndpointsEnum";
import INotification from "../../../../models/INotification";
import IOrder_Progress_State from "../../../../models/IOrder_Progress_State";
import IWorkflow from "../../../../models/IWorkflow";
import NotificationLevelEnum from "../../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../../models/NotificationPositionEnum";
import OwButton from "../../../shared/OwButton/OwButton";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import { EditWorkflowModal } from "../EditWorkflowModal/EditWorkflowModal";
import { WorkflowEditor } from "./WorkflowEditor";
import { WorkflowTracker } from "./WorkflowTracker";
import { orderedWorkflowSteps } from "./workflowSteps";
import { useSettingsPage } from "../SettingsPage/SettingsPageContext";

type Props = {
    socket: Socket;
    notifyFn: (data: IErrorDTO | null, _notification?: any, isFormatted?: boolean) => void;
    setActivityIndicator: (activityIndicator: boolean) => void;
};

const modalStyle: Styles = {
    content: {
        top: "10px",
        left: "10px",
        right: "10px",
        bottom: "10px",
        color: "var(--main-text-color)",
        backgroundColor: "var(--main-bg-color)",
        border: "1px solid var(--minor-bg-color)",
    },
    overlay: { zIndex: 100 },
};

/**
 * The workflows a business runs when multiple workflows are on: one row each with its steps,
 * rename, duplicate and delete, and Manage opens the shared editor inline under the row.
 * Each workflow saves on its own, separate from the page's Save changes.
 */
export const WorkflowList = (props: Props) => {
    const intl = useIntl();
    const supplierModel = useContext(SupplierContext);
    const userModel = useContext(UserContext);

    const [workflows, setWorkflows] = useState<IWorkflow[]>([]);
    const [openGuid, setOpenGuid] = useState<string | null>(null);
    const [working, setWorking] = useState<IOrder_Progress_State[] | null>(null);
    const [modal, setModal] = useState<React.ReactNode>(null);
    const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null);
    const confirmTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    // The workflow whose save is waiting for the server; the editor stays open until it answers.
    const pendingSave = useRef<string | null>(null);
    const [saving, setSaving] = useState(false);

    const t = (id: string, values?: Record<string, any>) => intl.formatMessage({ id, defaultMessage: `_${id}_` }, values);

    const getWorkflows = () => {
        const payload: IGetWorkflowsDTO = { userGuid: userModel.guid, userToken: userModel.token, supplierGuid: supplierModel.guid };

        props.socket.emit(EndpointsEnum.WorkflowsGetWorkflows, payload);
        props.setActivityIndicator(true);
    };

    useEffect(() => {
        const onGetWorkflows = (data: IOnGetWorkflowsDTO) => {
            props.setActivityIndicator(false);
            setWorkflows(data.workflows || []);
        };

        const onProcessWorkflow = () => {
            // Only a save closes the editor; duplicate and delete come through here too.
            if (pendingSave.current) {
                pendingSave.current = null;
                setSaving(false);

                const notification: INotification = {
                    message: "successfully_saved_workflow",
                    position: NotificationPositionEnum.bc,
                    level: NotificationLevelEnum.success,
                };

                props.notifyFn(null, notification);
                setOpenGuid(null);
                setWorking(null);
            }

            getWorkflows();
            // The supplier model carries the default workflow id, so refresh it too.
            const payload: ILoadUserDTO = { userGuid: userModel.guid, userToken: userModel.token, supplierGuid: supplierModel.guid };

            props.socket.emit(EndpointsEnum.AppLoadUser, payload);
        };

        // A failed save answers through the app's error channel, not the process reply; free the editor to retry.
        const onNotify = (data: IErrorDTO | null) => {
            if (pendingSave.current && data?.message === "ERROR_PROCESSING_WORKFLOW") {
                pendingSave.current = null;
                setSaving(false);
            }
        };

        props.socket.on(EndpointsEnum.WorkflowsOnGetWorkflows, onGetWorkflows);
        props.socket.on(EndpointsEnum.WorkflowsOnProcessWorkflow, onProcessWorkflow);
        props.socket.on(EndpointsEnum.Notify, onNotify);
        getWorkflows();

        return () => {
            props.socket.removeListener(EndpointsEnum.WorkflowsOnGetWorkflows, onGetWorkflows);
            props.socket.removeListener(EndpointsEnum.WorkflowsOnProcessWorkflow, onProcessWorkflow);
            props.socket.removeListener(EndpointsEnum.Notify, onNotify);

            if (confirmTimer.current) {
                clearTimeout(confirmTimer.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const processWorkflow = (workflow: IWorkflow, deleteFlag?: boolean) => {
        const payload: IProcessWorkflowDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            workflow,
            deleteFlag,
        };

        props.socket.emit(EndpointsEnum.WorkflowsProcessWorkflow, payload);
    };

    const open = workflows.find((workflow) => workflow.guid === openGuid) || null;
    const hasChanges = !!open && !!working && !_.isEqual(orderedWorkflowSteps(open.detail), orderedWorkflowSteps(working));

    // Tell the page header about unsaved work here, so it counts it and its Save and Discard reach it.
    const page = useSettingsPage();
    const actions = useRef({ save: () => {}, discard: () => {} });

    useEffect(() => {
        if (!open || !hasChanges) {
            return;
        }

        return page.registerPending({
            key: `workflow-${open.guid}`,
            title: open.name,
            rowId: "row-has_multi_workflows",
            save: () => actions.current.save(),
            discard: () => actions.current.discard(),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open?.guid, open?.name, hasChanges, page.registerPending]);

    const manage = (workflow: IWorkflow) => {
        setOpenGuid(workflow.guid);
        setWorking(_.cloneDeep(workflow.detail || []));
    };

    const close = () => {
        pendingSave.current = null;
        setSaving(false);
        setOpenGuid(null);
        setWorking(null);
    };

    const saveOpen = () => {
        // One save at a time: a second click while the server is still answering would send it twice.
        if (!open || !working || pendingSave.current) {
            return;
        }

        pendingSave.current = open.guid;
        setSaving(true);
        processWorkflow({ ...open, detail: working });
    };

    const discardOpen = () => {
        if (open) {
            setWorking(_.cloneDeep(open.detail || []));
        }
    };

    actions.current = { save: saveOpen, discard: discardOpen };

    const duplicate = (workflow: IWorkflow) => {
        processWorkflow({
            name: t("workflow_copy_name", { name: workflow.name }),
            supplier_id: supplierModel.id,
            detail: _.cloneDeep(workflow.detail || []),
        } as IWorkflow);
    };

    const remove = (workflow: IWorkflow) => {
        if (confirmingDelete !== workflow.guid) {
            setConfirmingDelete(workflow.guid);

            if (confirmTimer.current) {
                clearTimeout(confirmTimer.current);
            }

            confirmTimer.current = setTimeout(() => setConfirmingDelete(null), 4000);

            return;
        }

        setConfirmingDelete(null);
        processWorkflow(workflow, true);

        if (openGuid === workflow.guid) {
            close();
        }
    };

    const closeModal = () => setModal(null);

    const createWorkflow = () => setModal(<EditWorkflowModal closeModalFn={closeModal} socket={props.socket} notifyFn={props.notifyFn} />);
    const renameWorkflow = (workflow: IWorkflow) =>
        setModal(<EditWorkflowModal closeModalFn={closeModal} socket={props.socket} notifyFn={props.notifyFn} workflow={workflow} />);

    return (
        <div className={"WorkflowList"} data-testid={"workflow-list"}>
            <div className={"WorkflowList-head"}>
                <div>
                    <div className={"WorkflowList-title"}>
                        <OwLabel title={"workflows"} noBottomMargin={true} />
                    </div>
                    <div className={"WorkflowList-help"}>
                        <OwLabel title={"help_text_workflows_list"} noBottomMargin={true} />
                    </div>
                </div>
                <OwButton
                    title={"new_workflow"}
                    className={"btn-outline-secondary"}
                    leftIconClass={"fad fa-plus"}
                    fn={createWorkflow}
                    disableWidthOnClick={true}
                    testId={"wf-new-workflow"}
                />
            </div>

            {workflows.length ? null : (
                <div className={"WorkflowList-empty"}>
                    <OwLabel title={"no_workflows_yet"} noBottomMargin={true} />
                </div>
            )}

            {workflows.map((workflow) => {
                const isDefault = !_.isNil(workflow.id) && String(workflow.id) === String(supplierModel.default_workflow_id);
                const isOpen = workflow.guid === openGuid;

                return (
                    <React.Fragment key={workflow.guid}>
                        <div className={"WorkflowList-row" + (isOpen ? " open" : "")} data-testid={`workflow-${workflow.guid}`}>
                            <div className={"WorkflowList-main"}>
                                <div className={"WorkflowList-name"}>
                                    {workflow.name}
                                    {isDefault ? (
                                        <span className={"SettingsChip brand"}>
                                            <i className={"fad fa-star"} aria-hidden={true} />
                                            <OwLabel title={"default"} noBottomMargin={true} />
                                        </span>
                                    ) : null}
                                    {isOpen ? (
                                        <span className={"SettingsChip brand"}>
                                            <OwLabel title={"editing"} noBottomMargin={true} />
                                        </span>
                                    ) : null}
                                </div>
                                <WorkflowTracker
                                    steps={orderedWorkflowSteps(isOpen && working ? working : workflow.detail || [])}
                                    small={true}
                                />
                            </div>
                            <div className={"WorkflowList-actions"}>
                                <OwButton
                                    centerIconClass={"fad fa-pen-to-square"}
                                    className={"btn-link text-muted"}
                                    ariaLabel={`${t("rename")} ${workflow.name}`}
                                    hoverTitle={t("rename")}
                                    fn={() => renameWorkflow(workflow)}
                                    disableWidthOnClick={true}
                                />
                                <OwButton
                                    centerIconClass={"fad fa-copy"}
                                    className={"btn-link text-muted"}
                                    ariaLabel={`${t("duplicate")} ${workflow.name}`}
                                    hoverTitle={t("duplicate")}
                                    fn={() => duplicate(workflow)}
                                    disableWidthOnClick={true}
                                />
                                {confirmingDelete === workflow.guid ? (
                                    <>
                                        <OwButton
                                            title={"cancel"}
                                            className={"btn-outline-secondary"}
                                            fn={() => setConfirmingDelete(null)}
                                            disableWidthOnClick={true}
                                            disableDblClickPrevention={true}
                                            testId={`wf-cancel-delete-${workflow.guid}`}
                                        />
                                        <OwButton
                                            title={"confirm_delete"}
                                            className={"btn-danger"}
                                            fn={() => remove(workflow)}
                                            disableWidthOnClick={true}
                                            disableDblClickPrevention={true}
                                            testId={`wf-confirm-delete-${workflow.guid}`}
                                        />
                                    </>
                                ) : (
                                    <OwButton
                                        centerIconClass={"fad fa-trash-can"}
                                        className={"btn-link text-muted"}
                                        ariaLabel={`${t("delete")} ${workflow.name}`}
                                        hoverTitle={t("delete")}
                                        fn={() => remove(workflow)}
                                        disableWidthOnClick={true}
                                        disableDblClickPrevention={true}
                                        testId={`wf-delete-${workflow.guid}`}
                                    />
                                )}
                                {isOpen ? (
                                    <OwButton
                                        title={"close"}
                                        className={"btn-outline-secondary"}
                                        leftIconClass={"fad fa-chevron-up"}
                                        fn={close}
                                        disableWidthOnClick={true}
                                        testId={`wf-close-${workflow.guid}`}
                                    />
                                ) : (
                                    <OwButton
                                        title={"manage"}
                                        className={"btn-outline-secondary"}
                                        fn={() => manage(workflow)}
                                        disableWidthOnClick={true}
                                        testId={`wf-manage-${workflow.guid}`}
                                    />
                                )}
                            </div>
                        </div>

                        {isOpen && working ? (
                            <div className={"WorkflowList-editor"} data-testid={`workflow-editor-${workflow.guid}`}>
                                <div className={"WorkflowList-bar"}>
                                    <i className={"fad fa-layer-group"} aria-hidden={true} />
                                    <h4>
                                        {workflow.name}
                                        <span className={"SettingsChip"}>
                                            {t("steps_count", { count: orderedWorkflowSteps(working).length })}
                                        </span>
                                    </h4>
                                    <span className={"WorkflowList-spacer"} />
                                    {hasChanges ? (
                                        <span className={"SettingsChip brand"} data-testid={"wf-unsaved"}>
                                            <span className={"WorkflowList-dot"} />
                                            <OwLabel title={"unsaved_in_this_workflow"} noBottomMargin={true} />
                                        </span>
                                    ) : null}
                                    <OwButton
                                        title={"discard"}
                                        className={"btn-outline-secondary"}
                                        leftIconClass={"fad fa-rotate-left"}
                                        disabled={!hasChanges}
                                        fn={discardOpen}
                                        disableWidthOnClick={true}
                                        testId={"wf-discard"}
                                    />
                                    <OwButton
                                        title={"save_workflow"}
                                        disabled={!hasChanges || saving}
                                        active={saving}
                                        disableOnActive={true}
                                        fn={saveOpen}
                                        disableWidthOnClick={true}
                                        testId={"wf-save-workflow"}
                                    />
                                </div>
                                <div className={"WorkflowList-editor-body"}>
                                    <WorkflowEditor
                                        steps={working}
                                        savedSteps={workflow.detail || []}
                                        supplierModel={supplierModel}
                                        onChange={setWorking}
                                    />
                                </div>
                            </div>
                        ) : null}
                    </React.Fragment>
                );
            })}

            <Modal
                isOpen={!!modal}
                contentLabel={"Orderwerks"}
                shouldCloseOnOverlayClick={false}
                className={"modalContentClass"}
                overlayClassName={"modalOverlayClass"}
                style={modalStyle}>
                {modal}
            </Modal>
        </div>
    );
};

export default WorkflowList;
